/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2023 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2023 - Finnish Meteorological Institute (FMI)
 * Copyright 2024 - The Norwegian Meteorological Institute (MET Norway)
 * */
import React from 'react';
import { CustomDialog, CustomIconButton, ToolHeader } from '@opengeoweb/shared';
import { ListItem } from '@opengeoweb/theme';
import TacOverview from './TacOverview';
import type { TafFromBackend } from '../../../types';
import { useTafTranslation } from '../../../utils/i18n';
import { AutoTaf } from '../../AutoTaf';

interface TacOverviewMobileProps {
  upcomingTafs: TafFromBackend[];
  currentTafs: TafFromBackend[];
  expiredTafs: TafFromBackend[];
  autoTafs?: TafFromBackend[];
  activeTaf?: TafFromBackend;
  defaultOpen?: boolean;
}

const TacOverviewMobile: React.FC<TacOverviewMobileProps> = ({
  upcomingTafs,
  currentTafs,
  expiredTafs,
  activeTaf,
  autoTafs,
  defaultOpen = false,
}: TacOverviewMobileProps) => {
  const { t } = useTafTranslation();
  const [isOpen, setIsOpen] = React.useState(defaultOpen);
  const onClose = (): void => setIsOpen(false);

  return (
    <>
      <CustomIconButton
        data-testid="tac-overview-mobile-button"
        isSelected={isOpen}
        onClick={(): void => setIsOpen(true)}
        sx={{
          display: { xs: 'inline-flex', sm: 'none' },
          position: 'absolute',
          right: '4px',
          top: '42px',
        }}
        variant="tool"
      >
        <ListItem />
      </CustomIconButton>
      <CustomDialog
        data-testid="tac-overview-mobile"
        fullWidth
        hasLayout={false}
        maxWidth="sm"
        onClose={onClose}
        open={isOpen}
        sx={{ height: '100%' }}
      >
        <ToolHeader
          isDraggable={false}
          onClose={onClose}
          size="small"
          title={t('tac-overview')}
        />
        <TacOverview
          activeTaf={activeTaf}
          currentTafs={currentTafs}
          expiredTafs={expiredTafs}
          hideTitle
          upcomingTafs={upcomingTafs}
        />
        {!!autoTafs && <AutoTaf activeTaf={activeTaf} tafList={autoTafs} />}
      </CustomDialog>
    </>
  );
};

export default TacOverviewMobile;
